import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Delete, Add, Search } from '@mui/icons-material';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import COLORS from '@/resources/style/Themes';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import AddUserContactsModal from './AddUserContactsModal';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import {
  StringKeys,
  SuccessStringKeys,
  ErrorStringKeys,
} from '@/resources/strings/StringKeys';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { useContactMutationError } from '@/hooks/useContactMutationError';
import Group from '@/models/Organization/Group';

// Types for DataGrid rows
export interface AuthIdContactRow {
  id: string;
  auth_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  role: string;
}

interface EmergencyRespondersPanelProps {
  contacts: AuthIdContactRow[];
  onAddContacts: (authIds: string[]) => Promise<void>;
  onDeleteContacts: (authIds: string[]) => Promise<void>;
  loading: boolean;
  groups: Group[];
  currentGroupId: string;
  existingContactAuthIds: string[];
  tabControl: React.ReactNode;
  emptyMessage?: string;
}

const EmergencyRespondersPanel = ({
  contacts,
  onAddContacts,
  onDeleteContacts,
  loading,
  groups,
  currentGroupId,
  existingContactAuthIds,
  tabControl,
  emptyMessage,
}: EmergencyRespondersPanelProps) => {
  const { t } = useTranslation();
  const { showSuccessToast } = useToast();
  const showContactMutationError = useContactMutationError();

  // Local state
  const [selectedContacts, setSelectedContacts] = useState<GridRowId[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [searchText, setSearchText] = useState('');

  // DataGrid columns
  const columns: GridColDef<AuthIdContactRow>[] = useMemo(
    () => [
      {
        field: 'userName',
        headerName: t(StringKeys.NAME),
        minWidth: 150,
        flex: 1,
        display: 'flex',
        valueGetter: (_, row) =>
          `${row.first_name} ${row.last_name}`.trim() || t(StringKeys.UNKNOWN),
      },
      {
        field: 'phone_number',
        headerName: t(StringKeys.PHONE),
        minWidth: 130,
        flex: 0.75,
        display: 'flex',
      },
      {
        field: 'role',
        headerName: t(StringKeys.ROLE),
        minWidth: 100,
        flex: 0.5,
        display: 'flex',
      },
    ],
    [t],
  );

  // Filter contacts based on search
  const filteredContacts = useMemo(() => {
    if (!searchText.trim()) return contacts;
    const searchLower = searchText.toLowerCase().trim();
    return contacts.filter((row) => {
      const fullName = `${row.first_name} ${row.last_name}`.toLowerCase();
      const phone = row.phone_number?.toLowerCase() || '';
      return fullName.includes(searchLower) || phone.includes(searchLower);
    });
  }, [contacts, searchText]);

  // Handle adding contacts
  const handleAdd = useCallback(
    async (authIds: string[]) => {
      if (authIds.length === 0) return;

      try {
        await onAddContacts(authIds);
        setShowAddModal(false);
        showSuccessToast(
          t(SuccessStringKeys.SUCCESSFULLY_ADDED_RESPONDERS, {
            count: authIds.length,
          }),
        );
      } catch (err) {
        console.error('Error adding contacts:', err);
        await showContactMutationError(
          err,
          ErrorStringKeys.FAILED_TO_ADD_RESPONDERS,
        );
      }
    },
    [onAddContacts, showSuccessToast, showContactMutationError],
  );

  // Handle deleting contacts
  const handleDelete = useCallback(() => {
    if (selectedContacts.length === 0) return;
    setShowConfirmModal(true);
  }, [selectedContacts]);

  const handleConfirmDelete = useCallback(async () => {
    try {
      // Get auth_ids for selected rows
      const authIdsToDelete = contacts
        .filter((row) => selectedContacts.includes(row.id))
        .map((row) => row.auth_id);

      await onDeleteContacts(authIdsToDelete);
      setSelectedContacts([]);
      setShowConfirmModal(false);
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_REMOVED_RESPONDERS, {
          count: authIdsToDelete.length,
        }),
      );
    } catch (err) {
      console.error('Error deleting contacts:', err);
      await showContactMutationError(
        err,
        ErrorStringKeys.FAILED_TO_REMOVE_RESPONDERS,
      );
    }
  }, [
    selectedContacts,
    contacts,
    onDeleteContacts,
    showSuccessToast,
    showContactMutationError,
  ]);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Tabs Controller */}
        {tabControl}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder={t(StringKeys.SEARCH_NAME_OR_PHONE)}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: COLORS.GREY }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ minWidth: 240 }}
          />
          {selectedContacts.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
            >
              {t(StringKeys.DELETE)} ({selectedContacts.length})
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => setShowAddModal(true)}
          >
            {t(StringKeys.ADD_NEW_RESPONDER)}
          </Button>
        </Box>
      </Box>
      {/* Bounded-height wrapper: the grid scrolls its rows internally and keeps
          its pagination footer pinned to the bottom instead of overflowing. */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <NavigatorDataGrid
          height="100%"
          rows={filteredContacts}
          columns={columns}
          getRowId={(row) => row.id}
          initialPageSize={10}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          rowSelectionModel={selectedContacts}
          onRowSelectionModelChange={setSelectedContacts}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  py: 8,
                }}
              >
                <Typography
                  sx={{
                    color: COLORS.GREY,
                    fontStyle: 'italic',
                    fontSize: 14,
                  }}
                >
                  {emptyMessage ||
                    t(StringKeys.NO_EMERGENCY_RESPONDERS_CONFIGURED)}
                </Typography>
              </Box>
            ),
          }}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: COLORS.SAFEGUARD_BLUE,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: COLORS.WHITE,
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row': {
              py: 0,
            },
            '& .MuiDataGrid-cell': {
              py: 0.5,
            },
          }}
        />
      </Box>

      <AddUserContactsModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onAdd={handleAdd}
        groups={groups}
        currentGroupId={currentGroupId}
        existingContactAuthIds={existingContactAuthIds}
        loading={loading}
      />

      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        confirmTitle={t(StringKeys.CONFIRM_REMOVAL)}
        confirmMessage={t(StringKeys.CONFIRM_DELETE_RESPONDERS, {
          count: selectedContacts.length,
        })}
        confirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default EmergencyRespondersPanel;
